<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import type { PickerContext } from '../../composables/usePickerRegistry'

    const props = defineProps<{
        pickerName: string
        value: string | null
        context?: PickerContext
    }>()

    const { get } = usePickerRegistry()

    const label = ref<string | null>(null)
    const loading = ref(false)
    const failed = ref(false)

    const isPlainValue = computed(() => props.pickerName === 'text' || props.pickerName === 'select')

    async function resolve() {
        if (!props.value) {
            label.value = null
            loading.value = false
            failed.value = false
            return
        }

        // Plain text/select: display the value directly, no resolution needed
        if (isPlainValue.value) {
            label.value = props.value
            loading.value = false
            failed.value = false
            return
        }

        const picker = get(props.pickerName)
        if (!picker) {
            // Picker not yet registered — show spinner, never show raw UUID
            loading.value = true
            label.value = null
            failed.value = false
            return
        }

        loading.value = true
        label.value = null
        failed.value = false
        try {
            label.value = await picker.resolveLabel(props.value, props.context ?? {})
        }
        catch {
            // Resolution failed — show error indicator, not raw UUID
            failed.value = true
        }
        finally {
            loading.value = false
        }
    }

    watch([() => props.value, () => props.pickerName, () => props.context?.identityId], resolve, { immediate: true })
</script>

<template>
    <span v-if="!value" class="text-grey">—</span>
    <q-spinner v-else-if="loading" size="xs" color="grey" />
    <span v-else-if="failed" class="text-negative text-caption">Error loading</span>
    <span v-else>{{ label }}</span>
</template>
